import Image from "next/image";
import Link from "next/link";
import { CalendarDays, ChevronRight, Clock, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from "react-icons/fa";
import { Container } from "@/components/common/Container";
import { LeadForm } from "@/components/forms/LeadForm";
import { projects } from "@/content/projects";
import { site } from "@/content/site";
import { ROUTES } from "@/config/routes";

export function Footer({ hideForm }: { hideForm: boolean }) {
  return (
    <>
      {!hideForm && (
        <section className="bg-[var(--page)] py-16 lg:py-24 border-t border-[var(--border-subtle)]">
          <Container>
            <div className="grid gap-8 ui-panel bg-[var(--surface-muted)] p-5 text-[var(--text-strong)] lg:grid-cols-[1.05fr_0.95fr] lg:p-10">
              <div className="min-w-0">
                <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--accent)]">Book a site visit</p>
                <h2 className="font-serif text-3xl font-medium leading-tight sm:text-4xl">Ready to evaluate a plot in person?</h2>
                <p className="mt-4 max-w-2xl leading-7 text-[var(--text-muted)]">
                  Share your requirements and our sales team will help you compare projects and schedule a site visit.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  {[
                    { Icon: CalendarDays, title: "Guided Site Visit", copy: "Expert assistance at every step" },
                    { Icon: ShieldCheck, title: "Trusted & Verified", copy: "Clear documentation and legal clarity" },
                    { Icon: Clock, title: "Quick Response", copy: "We get back within 24 hours" }
                  ].map(({ Icon, title, copy }) => (
                    <div key={title} className="min-w-0">
                      <span className="mb-3 grid size-10 place-items-center rounded-full bg-[var(--accent)] text-white">
                        <Icon size={19} />
                      </span>
                      <h3 className="text-sm font-black text-[var(--text-strong)]">{title}</h3>
                      <p className="mt-1 text-xs leading-5 text-[var(--text-muted)]">{copy}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col gap-3 rounded-[var(--radius-sm)] bg-[var(--page)] border border-[var(--border-subtle)] p-4 sm:flex-row sm:items-center">
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-[var(--accent)] text-white">
                    <Phone size={20} />
                  </span>
                  <p className="text-sm leading-6 text-[var(--text-muted)]">
                    Prefer quick assistance? Call us directly at{" "}
                    <a href={`tel:${site.phone.replaceAll(" ", "")}`} className="font-black text-[var(--text-strong)] transition hover:text-[var(--accent)]">
                      +91 {site.phone}
                    </a>
                  </p>
                </div>
              </div>
              <LeadForm />
            </div>
          </Container>
        </section>
      )}

      <footer className="bg-[var(--surface-raised)] border-t border-[var(--border-subtle)] py-16 text-[var(--text-inverse)]">
        <Container>
          <div className="grid gap-10 border-b border-white/10 pb-10 lg:grid-cols-[1.45fr_0.8fr_0.8fr_1.2fr]">
            <div>
              <Image src="/brand/swargaseema-logo-official.png" alt="Swargaseema" width={280} height={46} className="h-auto w-[220px] object-contain sm:w-[280px]" />
              <p className="mt-5 max-w-sm text-sm leading-7 text-white/70">
                Verified plotted communities near South Hyderabad with clear documentation, guided site visits, and a trust-first buying journey.
              </p>
              <div className="mt-5 flex gap-3">
                {[FaFacebook, FaInstagram, FaYoutube, FaLinkedin].map((Icon, index) => (
                  <a key={index} href="#" aria-label="Social profile" className="grid size-9 place-items-center rounded-full border border-white/20 text-white transition hover:border-[var(--accent)] hover:bg-[var(--accent)]">
                    <Icon size={17} />
                  </a>
                ))}
              </div>
            </div>
            <FooterColumn title="Projects">
              {projects.map((project) => (
                <FooterLink key={project.slug} href={ROUTES.PROJECT(project.slug)}>
                  {project.name}
                </FooterLink>
              ))}
            </FooterColumn>
            <FooterColumn title="Quick Links">
              <FooterLink href={ROUTES.ABOUT}>About Us</FooterLink>
              <FooterLink href={ROUTES.PROJECTS}>Projects</FooterLink>
              <FooterLink href={ROUTES.GALLERY}>Gallery</FooterLink>
              <FooterLink href={ROUTES.CONTACT_US}>Contact Us</FooterLink>
            </FooterColumn>
            <div>
              <h3 className="mb-5 text-xs font-black uppercase tracking-[0.14em] text-[var(--accent)]">Contact</h3>
              <div className="grid gap-3 text-sm leading-6 text-white/75">
                <a href={`tel:${site.phone.replaceAll(" ", "")}`} className="flex gap-3"><Phone size={16} className="mt-1 shrink-0" /> {site.phone}</a>
                <a href={`mailto:${site.email}`} className="flex gap-3"><Mail size={16} className="mt-1 shrink-0" /> {site.email}</a>
                <p className="flex gap-3"><MapPin size={16} className="mt-1 shrink-0" /> {site.address}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 pt-6 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between">
            <p>Copyright 2026 Swargaseema. All rights reserved.</p>
            <p>Project details are subject to final confirmation during enquiry.</p>
          </div>
        </Container>
      </footer>
    </>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-5 text-xs font-black uppercase tracking-[0.14em] text-[var(--accent)]">{title}</h3>
      <div className="grid gap-1">{children}</div>
    </div>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className="flex items-center justify-between gap-3 border-b border-white/5 py-2.5 text-sm text-white/75 transition hover:text-[var(--accent)]">
      {children} <ChevronRight size={14} />
    </Link>
  );
}
